import type { GridColDef } from "@mui/x-data-grid-premium";
import { useMemo, useState } from "react";
import DataSubmissionStatusTag from "@entrilia/shared/components/dataCollection/DataSubmissionStatusTag";
import FormattedDueDate from "@entrilia/shared/components/dataCollection/FormattedDueDate";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import {
  DataGridColumnHeadersWithToolbar,
  DataGridHeaderToolbarProvider,
} from "@entrilia/shared/components/grid/DataGridColumnHeadersWithToolbar";
import { getRowSelectionModelFromIds, getSelectedRowIdsFromModel } from "@entrilia/shared/components/grid/gridHelper";
import InlineItemsList from "@entrilia/shared/components/InlineItemsList";
import GridTextHoverLink from "@entrilia/shared/components/grid/GridTextHoverLink";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import { distinctBy } from "@entrilia/shared/utilities/arrayHelper";
import { convertISODate, convertISODateTime, convertISODateTimeHumanized } from "@entrilia/shared/utilities/dateUtils";
import { nameof } from "@entrilia/shared/utilities/typeHelper";
import type { RequestSubmissionAuditory, RequestSubmissionInfo } from "../../../../../api/types/dataCollectionTypes";
import useClientContext from "../../../../../context/useClientContext";
import { pageRoutes } from "../../../../../routes";
import { getCheckedGridSx } from "../../../../common/grid/gridStyles";
import { getRecipientName } from "../../dataCollectionsGridHelper";
import DataRequestPageContext from "./DataRequestPageContext";
import { ActionsCell } from "./dataRequestSubmissionsGridProvider";
import DataRequestSubmissionsGridToolbar from "./DataRequestSubmissionsGridToolbar";

interface RecipientNameCellProps {
  row: RequestSubmissionInfo;
  requestId: string;
}

const RecipientNameCell = ({ row, requestId }: RecipientNameCellProps) => {
  const { clientCode } = useClientContext();

  return (
    <GridTextHoverLink
      to={
        row.isRecipientMissing
          ? undefined
          : `/${clientCode}/${pageRoutes.portfolio}/${pageRoutes.dataSubmissions}/${row.id}?ref=request&refid=${requestId}`
      }
      text={getRecipientName(row)}
    />
  );
};

const getColumnDefinitions = (requestId: string): GridColDef<RequestSubmissionInfo>[] => [
  {
    field: nameof<RequestSubmissionInfo>("recipientName"),
    headerName: "Portfolio Company",
    flex: 1,
    minWidth: 160,
    renderCell: ({ row }) => <RecipientNameCell row={row} requestId={requestId} />,
  },
  {
    field: nameof<RequestSubmissionInfo>("status"),
    headerName: "Status",
    flex: 1,
    minWidth: 160,
    renderCell: ({ row }) => <DataSubmissionStatusTag status={row.status} />,
  },
  {
    field: nameof<RequestSubmissionInfo>("dueDate"),
    headerName: "Due Date",
    flex: 0.5,
    minWidth: 120,
    renderCell: ({ row }) =>
      row.status === "Completed" ? convertISODate(row.dueDate) : <FormattedDueDate dueDateIsoString={row.dueDate} />,
  },
  {
    field: nameof<RequestSubmissionInfo>("lastReminderSentAt"),
    headerName: "Last Reminder",
    flex: 0.75,
    minWidth: 160,
    valueFormatter: (value: string | undefined) => convertISODateTimeHumanized(value),
  },
  {
    field: nameof<RequestSubmissionInfo>("auditoryList"),
    headerName: "Recipients",
    flex: 1,
    minWidth: 160,
    renderCell: ({ row }) => {
      if (row.auditoryList.length === 0) {
        return "-";
      }

      return (
        <InlineItemsList<RequestSubmissionAuditory>
          displayCount={1}
          items={distinctBy(row.auditoryList, (c) => c.name)}
          propGetter={(c) => c.name}
        />
      );
    },
  },
  {
    field: nameof<RequestSubmissionInfo>("submittedByUserName"),
    headerName: "Submitted by",
    flex: 1,
    minWidth: 160,
  },
  {
    field: nameof<RequestSubmissionInfo>("submittedAt"),
    headerName: "Submitted at",
    flex: 0.5,
    minWidth: 160,
    valueFormatter: (value: string | undefined) => convertISODateTime(value),
  },
  {
    field: "actions",
    headerName: "Actions",
    resizable: false,
    width: 80,
    cellClassName: "grid-row-actions",
    align: "right",
    headerAlign: "right",
    renderCell: ({ row }) => <ActionsCell row={row} />,
  },
];

interface Props {
  requestId: string;
  rows: RequestSubmissionInfo[];
}

const DataRequestSubmissionsGrid = ({ requestId, rows }: Props) => {
  const { hasPermissionsToManage } = useDefinedContext(DataRequestPageContext);

  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  const columns = useMemo(() => getColumnDefinitions(requestId), [requestId]);

  const showToolbar = selectedRowIds.length > 0;

  return (
    <DataGridHeaderToolbarProvider
      showToolbar={showToolbar}
      selection={[selectedRowIds, setSelectedRowIds]}
      actions={<DataRequestSubmissionsGridToolbar allSubmissions={rows} selectedIds={selectedRowIds} />}
    >
      <DataGrid<RequestSubmissionInfo>
        rows={rows}
        columns={columns}
        checkboxSelection={hasPermissionsToManage}
        rowSelectionModel={getRowSelectionModelFromIds(selectedRowIds)}
        onRowSelectionModelChange={(selection) => setSelectedRowIds(getSelectedRowIdsFromModel(selection))}
        sx={getCheckedGridSx}
        noRowsText="No data submissions"
        disableColumnReorder
        disableColumnSorting
        slots={{ columnHeaders: DataGridColumnHeadersWithToolbar }}
      />
    </DataGridHeaderToolbarProvider>
  );
};

export default DataRequestSubmissionsGrid;
